import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  Receipt,
  Search,
  Trash2,
  Download,
  Grid3X3,
  List,
  Camera,
  RefreshCw,
  Key,
  Car,
  Eye,
  MoreHorizontal,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import DashboardLayout from '../components/Layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [caseThumbnails, setCaseThumbnails] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filteredCases = cases.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return [c.vorname, c.nachname, c.kennzeichen, c.marke, c.modell, c.id].some(f => (f || '').toLowerCase().includes(q));
    }
    return true;
  });

  const fetchCases = useCallback(async (showRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    if (showRefresh) setIsRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/cases`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCases(data);
        // Fetch thumbnails for each case
        data.forEach(async (c) => {
          try {
            const photosRes = await fetch(`${API_URL}/api/cases/${c.id}/photos`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (photosRes.ok) {
              const photos = await photosRes.json();
              if (photos.length > 0) {
                setCaseThumbnails(prev => ({ ...prev, [c.id]: photos[0] }));
              }
            }
          } catch {}
        });
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchCases();
  }, [navigate, fetchCases]);

  // Calculate stats from cases
  const stats = [
    { icon: FileText, label: 'Neue Fälle', value: cases.filter(c => c.status === 'Neu').length, color: 'blue' },
    { icon: Clock, label: 'In Bearbeitung', value: cases.filter(c => c.status === 'In Bearbeitung').length, color: 'yellow' },
    { icon: CheckCircle, label: 'Abgeschlossen', value: cases.filter(c => c.status === 'Abgeschlossen').length, color: 'green' },
    { icon: Receipt, label: 'Abgerechnet', value: cases.filter(c => c.status === 'Abgerechnet').length, color: 'purple' },
  ];

  const tableHeaders = [
    'FOTO',
    'KUNDE / AKTENZEICHEN',
    'ARBEITSSCHRITT',
    'AMTL. KFZ.',
    'HERSTELLER',
    'WERTE',
    'BEARBEITER',
    'STATUS',
    'AKTIONEN'
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Neu': return 'bg-blue-100 text-blue-700';
      case 'In Bearbeitung': return 'bg-amber-100 text-amber-700';
      case 'Abgeschlossen': return 'bg-emerald-100 text-emerald-700';
      case 'Abgerechnet': return 'bg-violet-100 text-violet-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="p-6 lg:p-8">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
            Willkommen zurück
          </h1>
          <p className="text-gray-500">
            Hier ist Ihre Übersicht für heute
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Access Banner */}
                <div className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] rounded-lg px-3 py-2 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-medium">GDV-Versicherungsdaten freischalten</span>
          </div>
          <Button className="bg-white/20 hover:bg-white/30 text-white text-xs gap-1.5 h-6 px-2.5 rounded">
            <Key className="w-3 h-3" />
            Freischalten
          </Button>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Search + Filter Row */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suche nach Name, Kennzeichen, Marke..."
                className="w-full h-9 pl-10 pr-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#29beff] focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value="">Alle Status</option>
              <option value="Neu">Neu</option>
              <option value="In Bearbeitung">In Bearbeitung</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
              <option value="Abgerechnet">Abgerechnet</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                className="gap-2 text-gray-600 border-gray-200"
                onClick={() => fetchCases(true)}
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
              <span className="text-sm text-gray-500">{filteredCases.length} Akten</span>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              >
                <Grid3X3 className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              >
                <List className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="p-4 text-left w-12">
                    <Checkbox />
                  </th>
                  {tableHeaders.map((header) => (
                    <th key={header} className="p-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={tableHeaders.length + 1} className="p-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                        <p className="text-gray-600 font-medium">Fälle werden geladen...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredCases.length === 0 ? (
                  <tr>
                    <td colSpan={tableHeaders.length + 1} className="p-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                          <Camera className="w-10 h-10 text-gray-300" />
                        </div>
                        <p className="text-gray-600 font-medium mb-1">Keine Akten vorhanden</p>
                        <p className="text-gray-400 text-sm">Erstellen Sie Ihren ersten Fall über "+ Neuer Fall"</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((caseItem) => (
                    <tr 
                      key={caseItem.id} 
                      className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/fall/${caseItem.id}`)}
                    >
                      <td className="p-4">
                        <Checkbox />
                      </td>
                      <td className="p-4">
                        {caseThumbnails[caseItem.id] ? (
                          <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                            <img src={`${API_URL}${caseThumbnails[caseItem.id].url}`} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Car className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{caseItem.vorname && caseItem.nachname ? `${caseItem.vorname} ${caseItem.nachname}` : 'Neue Akte'}</p>
                          <p className="text-sm text-gray-500">#{caseItem.id.slice(0, 8).toUpperCase()}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600 capitalize">{caseItem.auftragstyp}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-sm text-gray-900">{caseItem.kennzeichen || '-'}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-900">{caseItem.marke || '-'}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600">{caseItem.laufleistung ? `${parseInt(caseItem.laufleistung).toLocaleString('de-DE')} km` : '-'}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600">{user?.name || 'Admin'}</span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={() => navigate(`/fall/${caseItem.id}`)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors" 
                            title="Ansehen"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Mehr">
                            <MoreHorizontal className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-500',
    yellow: 'bg-amber-50 text-amber-500',
    green: 'bg-emerald-50 text-emerald-500',
    purple: 'bg-violet-50 text-violet-500',
  };

  const borderColors = {
    blue: 'border-blue-100',
    yellow: 'border-amber-100',
    green: 'border-emerald-100',
    purple: 'border-violet-100',
  };

  return (
    <div className={`bg-white rounded-xl border ${borderColors[color]} p-5 flex items-center gap-4 hover:shadow-md transition-shadow`}>
      <div className={`w-12 h-12 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
};

export default DashboardPage;
